import { memo, useEffect, useRef } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  DayPart,
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated';

import { PRIMARY } from 'constants/colors';
import { CARD_WIDTH } from 'constants/widths';

import { formatSecondsToHoursMinutes } from 'utils/date';

import { Box } from '@mui/material';
import { brightness } from 'utils/misc';
import {
  panelThemeColors,
  PanelTheme
} from '../../components/ActivityPanel/constants';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';

interface FreeTimePragmaticProps {
  id: string;
  dayPart: DayPart;
  scheduleStart: string;
  start: string;
  durationInSeconds: number;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier: PlanningEntityIdentifier;
}

function FreeTimePragmatic({
  id: _id,
  dayPart = 'MORNING',
  scheduleStart,
  start,
  durationInSeconds,
  planningEntityType,
  planningEntityId,
  planningEntityIdentifier
}: FreeTimePragmaticProps) {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const cleanup = dropTargetForElements({
      element: el,
      getData: () => ({
        scheduleStart,
        start,
        planningEntityType,
        planningEntityId,
        planningEntityIdentifier
      }),
      onDragEnter: () => el.setAttribute('data-drag-over', ''),
      onDragLeave: () => el.removeAttribute('data-drag-over'),
      onDrop: () => el.removeAttribute('data-drag-over')
    });
    return () => {
      cleanup();
      el.removeAttribute('data-drag-over');
    };
  }, [
    scheduleStart,
    start,
    planningEntityId,
    planningEntityType,
    planningEntityIdentifier
  ]);

  const { borderColor, textColor } = panelThemeColors[dayPart as PanelTheme];

  return (
    <Box
      component="li"
      ref={ref}
      className="freetime-container"
      sx={{
        height: '45px',
        margin: '-10px 0',
        padding: '10px 0',
        width: CARD_WIDTH,
        position: 'relative',
        zIndex: 1,
        '&:first-of-type': {
          marginTop: '-20px'
        },
        '&:last-of-type': {
          margin: '-10px 0'
        },
        '&:last-of-type:not(:first-of-type)': {
          marginTop: '0'
        },
        '+ .traveltime-container': {
          marginTop: '-25px'
        },
        '&[data-drag-over] .freetime': {
          backgroundColor: brightness(PRIMARY, 90),
          border: `2px solid ${PRIMARY}`
        }
      }}
    >
      <Box
        display="flex"
        flexShrink={0}
        justifyContent="center"
        alignItems="center"
        className="freetime"
        my={1}
        sx={{
          boxSizing: 'border-box',
          height: '30px',
          width: '100%',
          color: textColor,
          backgroundColor: 'transparent',
          fontSize: '12px',
          fontFamily: 'SourceSansPro-Regular',
          borderRadius: '4px',
          border: `1px dashed ${borderColor}`,
          transition: 'border-color 0.2s ease-in-out'
        }}
      >
        <StyledExtraSmallIcon type="clock" sx={{ marginRight: '8px' }} />
        {`${formatSecondsToHoursMinutes(durationInSeconds)} vrije ruimte`}
      </Box>
    </Box>
  );
}

export default memo(FreeTimePragmatic);
